import React, { use, useMemo } from "react";
import { useAtom, useSetAtom } from "jotai";
import ScreenCard from "../ScreenCard";
import { lastValidatedWorkingTime, newMissionState } from "../../atoms/core";
import { CreateMissionScreensContext } from "../../contexts/CreateMissionManager";
import { useI18n } from "../../hooks/i18n-hooks";
import WorkingTimeEditor from "../../components/working_time/WorkingTimeEditor";
import { MAX_HOURS_PER_WEEK } from "../../utils";
import {
    isWorkingTimeClassicSpecFilled,
    useHasErrorWorkingTimeClassicSpec,
} from "../../components/working_time/helpers";
import { Contract, Unit, WorkingTimeClassicType } from "../../__generated__/graphql";
import { Bubble } from "../../components/Bubble";
import { useBubbleStepOrigin } from "../../hooks/use-bubble-step-origin";
import { useContractCct } from "../../hooks/cct-utils";

const CreateMissionWorkingTimeMore = () => {
    const i18n = useI18n();
    const [mission, setMission] = useAtom(newMissionState);

    const setLastValidatedHoursPerWeekFullTime = useSetAtom(lastValidatedWorkingTime);

    const { validateScreen } = use(CreateMissionScreensContext);
    const validate = React.useCallback(() => {
        const workingSpec = mission.currentContract?.workingTimeClassicSpec;
        setLastValidatedHoursPerWeekFullTime({
            hoursPerWeekFullTime: mission.currentContract?.hoursPerWeekFullTime ?? undefined,
            workingSpec:
                workingSpec?.repetition && workingSpec?.unit
                    ? (workingSpec as WorkingTimeClassicType)
                    : undefined,
        });
        void validateScreen("CreateMissionWorkingTimeMore");
    }, [
        validateScreen,
        mission.currentContract?.hoursPerWeekFullTime,
        mission.currentContract?.workingTimeClassicSpec,
        setLastValidatedHoursPerWeekFullTime,
    ]);

    const hasErrorWorkingTimeClassicSpec = useHasErrorWorkingTimeClassicSpec();
    const missionCct = useContractCct(mission.currentContract as Contract);
    const { hasError } = useMemo(() => {
        const hoursPerWeek = missionCct?.hoursPerWeek || MAX_HOURS_PER_WEEK;
        return hasErrorWorkingTimeClassicSpec(
            mission.currentContract?.workingTimeClassicSpec?.unit || Unit.Hours,
            mission.currentContract?.workingTimeClassicSpec?.repetition,
            Number(mission.currentContract?.workingTimeClassicSpec?.unitCount),
            hoursPerWeek,
            Number(mission.currentContract?.workingTimeClassicSpec?.dailyHours),
        );
    }, [
        mission.currentContract?.workingTimeClassicSpec?.unit,
        mission.currentContract?.workingTimeClassicSpec?.repetition,
        mission.currentContract?.workingTimeClassicSpec?.unitCount,
        mission.currentContract?.workingTimeClassicSpec?.dailyHours,
        hasErrorWorkingTimeClassicSpec,
        missionCct,
    ]);

    const isValid = useMemo(
        () =>
            isWorkingTimeClassicSpecFilled(mission.currentContract?.workingTimeClassicSpec) &&
            !hasError,
        [hasError, mission.currentContract?.workingTimeClassicSpec],
    );

    const origin = useBubbleStepOrigin("CreateMissionWorkingTimeMore");

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.translate("next"),
                    onPress: validate,
                    isValid: isValid,
                },
            ]}
            withAssistanceButton
        >
            <Bubble
                title={i18n.t("screens.user.create_mission_working_time_more.title")}
                origin={origin}
                bubbleStyle={{ paddingBottom: 0 }}
            >
                <WorkingTimeEditor mission={mission} setMission={setMission} />
            </Bubble>
        </ScreenCard>
    );
};

export default CreateMissionWorkingTimeMore;
